import { z } from "zod";
import type { FieldValues } from "react-hook-form";
import {
  DOCUMENT_TYPES,
  INCOME_SOURCES,
  OCCUPATIONS,
  OPEN_ACCOUNT_REASONS,
} from "../interfaces/kyc.interface";
import {
  isP100SupportedCountryCode,
  P100_SUPPORTED_COUNTRY_ERROR_MESSAGE,
} from "../constants/supported-countries";

const requiredEnumValue = <TValues extends readonly [string, ...string[]]>(
  values: TValues,
  message: string
) =>
  z
    .string()
    .trim()
    .min(1, message)
    .refine((value) => values.includes(value as TValues[number]), { message })
    .transform((value) => value as TValues[number]);

const optionalOccupationSchema = z
  .string()
  .optional()
  .transform((value) => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
  });

export const retryKycSchema = z
  .object({
    documentType: requiredEnumValue(
      DOCUMENT_TYPES,
      "Document type is required."
    ),
    country: z
      .string()
      .trim()
      .min(1, "Country is required.")
      .length(2, "Use the 2-letter ISO code")
      .regex(/^[A-Z]{2}$/, "Only uppercase letters are allowed")
      .refine(isP100SupportedCountryCode, {
        message: P100_SUPPORTED_COUNTRY_ERROR_MESSAGE,
      }),
    incomeSource: requiredEnumValue(
      INCOME_SOURCES,
      "Income source is required."
    ),
    openAccountReason: requiredEnumValue(
      OPEN_ACCOUNT_REASONS,
      "Reason is required."
    ),
    occupation: optionalOccupationSchema,
  })
  .superRefine((values, ctx) => {
    if (
      values.occupation &&
      !OCCUPATIONS.includes(values.occupation as never)
    ) {
      ctx.addIssue({
        path: ["occupation"],
        code: z.ZodIssueCode.custom,
        message: "Select a valid occupation.",
      });
    }

    if (values.incomeSource === "SALARY" && !values.occupation) {
      ctx.addIssue({
        path: ["occupation"],
        code: z.ZodIssueCode.custom,
        message: "Occupation is required for salary income.",
      });
    }
  });

export type RetryKycFormValues = z.input<typeof retryKycSchema> & FieldValues;
